import { faCircleCheck, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { Button, Switch } from '@repo/videa-components';

import type { FieldDefinition } from '@/react-query/videa-notes/useVoiceNoteFields';

import { formatFieldKeyForDisplay } from './fieldDisplayHelpers';
import { FieldValuesList } from './FieldValuesList';

export interface FieldDetailContentProps {
  field: FieldDefinition;
  changesSavedAt: number | null;
  suggestedValues: string[];
  defaultDisplayValue: string | null;
  isSetDefaultPending: boolean;
  isSetRequiredPending: boolean;
  onAddValue: () => void;
  onSetDefault: (value: string) => void;
  onSetRequired: (required: boolean) => void;
}

export const FieldDetailContent = ({
  field,
  changesSavedAt,
  suggestedValues,
  defaultDisplayValue,
  isSetDefaultPending,
  isSetRequiredPending,
  onAddValue,
  onSetDefault,
  onSetRequired,
}: FieldDetailContentProps) => (
  <div className="vai-flex-1 vai-flex vai-flex-col vai-overflow-auto">
    <div className="vai-flex vai-items-center vai-justify-between vai-gap-3 vai-mb-1">
      <h2 className="vai-text-lg vai-font-semibold vai-text-gray-900 vai-min-w-0 vai-truncate">
        {field.label || formatFieldKeyForDisplay(field.key)}
      </h2>
      <div className="vai-flex vai-shrink-0 vai-items-center vai-gap-3">
        <span
          className={`vai-inline-flex vai-items-center vai-gap-2 vai-text-sm vai-font-medium vai-text-leaf-600 vai-transition-opacity vai-duration-200 vai-ease-out ${
            changesSavedAt != null
              ? 'vai-opacity-100'
              : 'vai-opacity-0 vai-pointer-events-none'
          }`}
        >
          <FontAwesomeIcon
            icon={faCircleCheck}
            className="vai-h-4 vai-w-4 vai-shrink-0"
          />
          <Trans>Changes Saved</Trans>
        </span>
        <span className="vai-text-sm vai-font-medium vai-text-gray-900">
          <Trans>Required</Trans>
        </span>
        <Switch
          checked={field.required}
          onCheckedChange={onSetRequired}
          disabled={isSetRequiredPending}
        />
      </div>
    </div>
    {field.description ? (
      <p className="vai-text-sm vai-text-gray-600 vai-mb-4">
        {field.description}
      </p>
    ) : null}
    <div className="vai-flex vai-items-center vai-justify-between vai-mb-3">
      <span className="vai-text-sm vai-font-medium vai-text-gray-900">
        <Trans>Field Values</Trans>
      </span>
      <Button variant="outline" size="sm" onClick={onAddValue}>
        <FontAwesomeIcon icon={faPlus} />
        <Trans>Add Value</Trans>
      </Button>
    </div>
    <FieldValuesList
      values={suggestedValues}
      defaultDisplayValue={defaultDisplayValue}
      onSetDefault={onSetDefault}
      isPending={isSetDefaultPending}
    />
  </div>
);
